<template>
  <div class="automations-view">
    <n-page-header title="Automations">
      <template #extra>
        <n-button type="primary" @click="createAutomation">
          <template #icon>
            <n-icon><AddIcon /></n-icon>
          </template>
          Create Automation
        </n-button>
      </template>
    </n-page-header>

    <!-- Filters -->
    <n-card class="filter-card">
      <n-space>
        <n-select
          v-model:value="filters.status"
          placeholder="Status"
          :options="statusOptions"
          clearable
          style="width: 150px"
        />
        <n-select
          v-model:value="filters.trigger_type"
          placeholder="Trigger Type"
          :options="triggerOptions"
          clearable
          style="width: 180px"
        />
        <n-input
          v-model:value="filters.search"
          placeholder="Search automations..."
          clearable
          style="width: 250px"
        />
      </n-space>
    </n-card>

    <!-- Automations List -->
    <n-card>
      <n-data-table
        :columns="columns"
        :data="filteredAutomations"
        :loading="loading"
        :pagination="pagination"
        :row-key="row => row.id"
      />
    </n-card>

    <!-- Create/Edit Modal -->
    <n-modal v-model:show="showModal" preset="card" :title="modalTitle" style="width: 500px">
      <n-form :model="form" :rules="rules" ref="formRef">
        <n-form-item label="Name" path="name">
          <n-input v-model:value="form.name" placeholder="My Automation" />
        </n-form-item>
        <n-form-item label="Description" path="description">
          <n-input
            v-model:value="form.description"
            type="textarea"
            placeholder="What does this automation do?"
          />
        </n-form-item>
        <n-form-item label="Trigger Type" path="trigger_type">
          <n-select
            v-model:value="form.trigger_type"
            :options="triggerOptions"
            placeholder="Select trigger"
          />
        </n-form-item>
      </n-form>
      <template #footer>
        <n-space justify="end">
          <n-button @click="showModal = false">Cancel</n-button>
          <n-button type="primary" @click="saveAutomation" :loading="saving">
            {{ editingId ? 'Update' : 'Create' }}
          </n-button>
        </n-space>
      </template>
    </n-modal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, h } from 'vue'
import { useRouter } from 'vue-router'
import {
  NPageHeader, NCard, NSpace, NButton, NIcon, NSelect, NInput,
  NDataTable, NModal, NForm, NFormItem, NTag, NDropdown
} from 'naive-ui'
import { useToast } from '@shared/composables/useToast.js'
import { Add as AddIcon, PlayCircle, PauseCircle, Settings, Trash } from '@vicons/ionicons5'
import { useAdmin } from '@/composables/useAdmin'

const router = useRouter()
const toast = useToast()
const { api } = useAdmin()

// State
const loading = ref(false)
const saving = ref(false)
const automations = ref([])
const showModal = ref(false)
const editingId = ref(null)
const formRef = ref(null)

const filters = ref({
  status: null,
  trigger_type: null,
  search: ''
})

const form = ref({
  name: '',
  description: '',
  trigger_type: 'manual'
})

// Options
const statusOptions = [
  { label: 'Draft', value: 'draft' },
  { label: 'Active', value: 'active' },
  { label: 'Paused', value: 'paused' },
  { label: 'Archived', value: 'archived' }
]

const triggerOptions = [
  { label: 'Manual', value: 'manual' },
  { label: 'Webhook', value: 'webhook' },
  { label: 'Schedule', value: 'schedule' },
  { label: 'WhatsApp Message', value: 'whatsapp' },
  { label: 'Domain Event', value: 'event' }
]

// Computed
const modalTitle = computed(() => editingId.value ? 'Edit Automation' : 'Create Automation')

const filteredAutomations = computed(() => {
  return automations.value.filter(a => {
    if (filters.value.status && a.status !== filters.value.status) return false
    if (filters.value.trigger_type && a.trigger_type !== filters.value.trigger_type) return false
    if (filters.value.search) {
      const search = filters.value.search.toLowerCase()
      if (!a.name.toLowerCase().includes(search) &&
          !(a.description || '').toLowerCase().includes(search)) {
        return false
      }
    }
    return true
  })
})

const pagination = ref({
  page: 1,
  pageSize: 10,
  showSizePicker: true,
  pageSizes: [10, 20, 50]
})

// Table columns
const columns = [
  {
    title: 'Name',
    key: 'name',
    render: (row) => h('a', {
      style: 'cursor: pointer; color: var(--primary-color)',
      onClick: () => openEditor(row.id)
    }, row.name)
  },
  {
    title: 'Trigger',
    key: 'trigger_type',
    render: (row) => h(NTag, {
      type: getTriggerTagType(row.trigger_type),
      size: 'small'
    }, () => row.trigger_type)
  },
  {
    title: 'Status',
    key: 'status',
    render: (row) => h(NTag, {
      type: getStatusTagType(row.status),
      size: 'small'
    }, () => row.status)
  },
  {
    title: 'Last Run',
    key: 'last_run_at',
    render: (row) => row.last_run_at ? new Date(row.last_run_at).toLocaleString('en-IN') : '-'
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 150,
    render: (row) => h(NSpace, null, () => [
      h(NButton, {
        size: 'small',
        quaternary: true,
        onClick: () => openEditor(row.id)
      }, () => 'Edit'),
      h(NDropdown, {
        trigger: 'click',
        options: getActionOptions(row),
        onSelect: (key) => handleAction(key, row)
      }, () => h(NButton, { size: 'small', quaternary: true }, () => '...'))
    ])
  }
]

// Rules
const rules = {
  name: { required: true, message: 'Name is required' },
  trigger_type: { required: true, message: 'Trigger type is required' }
}

// Methods
function getTriggerTagType(type) {
  const types = {
    manual: 'default',
    webhook: 'info',
    schedule: 'warning',
    whatsapp: 'success',
    event: 'primary'
  }
  return types[type] || 'default'
}

function getStatusTagType(status) {
  const types = {
    draft: 'default',
    active: 'success',
    paused: 'warning',
    archived: 'error'
  }
  return types[status] || 'default'
}

function getActionOptions(row) {
  const options = []

  if (row.status === 'active') {
    options.push({ label: 'Pause', key: 'pause' })
  } else if (row.status === 'draft' || row.status === 'paused') {
    options.push({ label: 'Activate', key: 'activate' })
  }

  if (row.trigger_type === 'manual') {
    options.push({ label: 'Run Now', key: 'run' })
  }

  options.push({ label: 'Duplicate', key: 'duplicate' })
  options.push({ type: 'divider' })
  options.push({ label: 'Delete', key: 'delete' })

  return options
}

async function handleAction(key, row) {
  switch (key) {
    case 'activate':
      await updateStatus(row.id, 'active')
      break
    case 'pause':
      await updateStatus(row.id, 'paused')
      break
    case 'run':
      await runAutomation(row.id)
      break
    case 'duplicate':
      await duplicateAutomation(row)
      break
    case 'delete':
      await deleteAutomation(row.id)
      break
  }
}

async function fetchAutomations() {
  loading.value = true
  try {
    const response = await api.get('/automation/automations')
    automations.value = response.data.automations || []
  } catch (error) {
    toast.error('Failed to load automations')
  } finally {
    loading.value = false
  }
}

function createAutomation() {
  editingId.value = null
  form.value = { name: '', description: '', trigger_type: 'manual' }
  showModal.value = true
}

function openEditor(id) {
  router.push(`/automations/${id}/edit`)
}

async function saveAutomation() {
  try {
    await formRef.value?.validate()
  } catch {
    return
  }

  saving.value = true
  try {
    if (editingId.value) {
      await api.put(`/automation/automations/${editingId.value}`, form.value)
      toast.success('Automation updated')
    } else {
      const response = await api.post('/automation/automations', form.value)
      toast.success('Automation created')
      // Open editor for new automation
      router.push(`/automations/${response.data.id}/edit`)
    }
    showModal.value = false
    await fetchAutomations()
  } catch (error) {
    toast.error(error.response?.data?.detail || 'Failed to save automation')
  } finally {
    saving.value = false
  }
}

async function updateStatus(id, status) {
  try {
    await api.patch(`/automation/automations/${id}/status`, { status })
    toast.success(`Automation ${status}`)
    await fetchAutomations()
  } catch (error) {
    toast.error('Failed to update status')
  }
}

async function runAutomation(id) {
  try {
    await api.post(`/automation/automations/${id}/execute`)
    toast.success('Automation started')
  } catch (error) {
    toast.error('Failed to run automation')
  }
}

async function duplicateAutomation(row) {
  try {
    await api.post(`/automation/automations/${row.id}/duplicate`)
    toast.success('Automation duplicated')
    await fetchAutomations()
  } catch (error) {
    toast.error('Failed to duplicate automation')
  }
}

async function deleteAutomation(id) {
  try {
    await api.delete(`/automation/automations/${id}`)
    toast.success('Automation deleted')
    await fetchAutomations()
  } catch (error) {
    toast.error('Failed to delete automation')
  }
}

onMounted(fetchAutomations)
</script>

<style scoped>
.automations-view {
  padding: 24px;
}

.filter-card {
  margin: 16px 0;
}
</style>
